import { useState } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { RouterLinkAs } from '@/lib/router-link';
import { requirePlatformRole } from '@/lib/route-guards';
import {
  Body,
  Button,
  Card,
  Field,
  I,
  Input,
  PageHeader,
  RadioCard,
  RadioCardGroup,
  Row,
  Screen,
  ScreenBody,
  Stack,
  Textarea,
} from '@leanin/ui';
import { useCreateSpace } from '@/lib/mutations/spaces';
import { failureFromError } from '@/lib/api-error';

type Access = 'public' | 'unlisted' | 'private';
type JoinPolicy = 'open' | 'request' | 'invite_only';

export const Route = createFileRoute('/_authed/groups/new')({
  // Creating a group is admin-only (the server enforces it on POST
  // /api/v1/spaces). Redirect non-admins who reach the form by URL so they
  // never hit a 403 they can't act on.
  beforeLoad: async ({ context }) => {
    await requirePlatformRole(context, {
      role: 'admin',
      bounceTo: () => redirect({ to: '/groups' }),
    });
  },
  component: GroupCreatePage,
});

function GroupCreatePage() {
  const navigate = useNavigate();
  const createSpace = useCreateSpace();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [access, setAccess] = useState<Access>('public');
  const [joinPolicy, setJoinPolicy] = useState<JoinPolicy>('open');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const canSubmit = name.trim().length > 0;

  const onSubmit = async () => {
    setSubmitError(null);
    if (!canSubmit) {
      setSubmitError('Please give your group a name.');
      return;
    }
    let detail;
    try {
      detail = await createSpace.mutateAsync({
        name: name.trim(),
        access,
        joinPolicy,
        ...(description.trim().length > 0
          ? { description: description.trim() }
          : {}),
      });
    } catch (err) {
      setSubmitError(failureFromError(err, 'Failed to create group.').message);
      return;
    }
    navigate({ to: '/groups/$id', params: { id: detail.id } });
  };

  const isPending = createSpace.isPending;

  const actions = (
    <Row gap="sm">
      <Button href="/groups" linkAs={RouterLinkAs} variant="ghost">
        Cancel
      </Button>
      <Button
        variant="primary"
        iconRight={I.arrow}
        onClick={onSubmit}
        disabled={isPending || !canSubmit}
      >
        {isPending ? 'Creating…' : 'Create group'}
      </Button>
    </Row>
  );

  return (
    <Screen>
      <ScreenBody gap="xl">
        <PageHeader
          eyebrow="New group"
          title="Create a group"
          subtitle="Groups are open discussion spaces members can join to share news, ask thoughtful questions, and support one another."
          back={{ label: 'Groups', href: '/groups' }}
          actions={actions}
        />
        <Card pad="spacious">
          <Stack gap="lg">
            <Field required>
              <Field.Label>Group name</Field.Label>
              <Field.Description>
                Choose a clear name members will recognise.
              </Field.Description>
              <Field.Control>
                <Input
                  placeholder="e.g. Career Pivots"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Field.Control>
            </Field>
            <Field>
              <Field.Label>Description</Field.Label>
              <Field.Description>
                Optional — what the group is for and who it’s for.
              </Field.Description>
              <Field.Control>
                <Textarea
                  rows={3}
                  placeholder="A space to trade advice on changing roles, industries, and career direction."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Field.Control>
            </Field>
            <Field required>
              <Field.Label>Who can find and join?</Field.Label>
              <Field.Control>
                <RadioCardGroup
                  value={access}
                  onValueChange={(v) => setAccess(v as Access)}
                  columns={3}
                  aria-label="Who can find and join?"
                >
                  <RadioCard value="public" title="Public" />
                  <RadioCard value="unlisted" title="Unlisted" />
                  <RadioCard value="private" title="Private" />
                </RadioCardGroup>
              </Field.Control>
            </Field>
            <Field required>
              <Field.Label>Joining</Field.Label>
              <Field.Control>
                <RadioCardGroup
                  value={joinPolicy}
                  onValueChange={(v) => setJoinPolicy(v as JoinPolicy)}
                  columns={3}
                  aria-label="Joining"
                >
                  <RadioCard value="open" title="Open" />
                  <RadioCard value="request" title="By request" />
                  <RadioCard value="invite_only" title="Invite only" />
                </RadioCardGroup>
              </Field.Control>
            </Field>

            {submitError !== null ? (
              <Card pad="default" tone="burgundy">
                <Body color="primary">{submitError}</Body>
              </Card>
            ) : null}

            <Row justify="flex-end">{actions}</Row>
          </Stack>
        </Card>
      </ScreenBody>
    </Screen>
  );
}
